// Locale-aware accessors for the /[lang]/wine-tours landing pages' hand-written
// copy. Same approach as cityPageCopy (city-content.ts): authored per locale in
// the i18n resource files and read off the imported JSON directly rather than
// through react-i18next's t(), which would need `returnObjects` handling for
// the `intro` / `faq` arrays.

import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

export type WinePageCopy = {
  /** On-page <h1>. */
  title: string;
  /** <title> base — fitted to the SERP budget by seoTitle() in seo.ts. */
  seoTitle: string;
  metaDescription: string;
  intro: string[];
  /** <h2> above the tour grid. Per page, since "from {city}" doesn't interpolate cleanly in FR/RU. */
  listHeading: string;
  /** <h2> above the private-tour cards — the page's "private wine tour {city}" phrase. Per page for the same reason. */
  privateHeading: string;
};

export type WineFaqEntry = { q: string; a: string };

/** <h2> sections below the regional hub's tour grid. */
export type WineSection = { heading: string; paragraphs: string[] };

type WineResource = {
  wineTours: {
    region: WinePageCopy & { sections: WineSection[] };
    cities: Record<string, WinePageCopy>;
    faq: WineFaqEntry[];
  };
};

const resources: Record<Locale, WineResource> = { en, fr, ru };

/**
 * Copy for the regional hub (no `cityId`) or one departure city's page.
 * Undefined for a city without authored copy.
 */
export function winePageCopy(locale: Locale, cityId?: string): WinePageCopy | undefined {
  const wine = resources[locale].wineTours;
  return cityId ? wine.cities[cityId] : wine.region;
}

/**
 * The regional hub's guide sections (wine regions, what a tour is like). Hub
 * only — repeated on all seven city pages it would be duplicate content.
 * Evergreen by design: no prices or counts, which drift while the page lives.
 */
export function wineRegionSections(locale: Locale): WineSection[] {
  return resources[locale].wineTours.region.sections;
}

/** The wine-tour FAQ shown on every wine page, for the on-page block + FAQPage JSON-LD. */
export function wineFaq(locale: Locale): WineFaqEntry[] {
  return resources[locale].wineTours.faq;
}
